import omit from "lodash/omit";

import { StrictLocalizeFunction } from "../localization";
import { LocalizationStore } from "../localization/LocalizationStore";

/**
 * Return an object from ISO code to localized names, in localized order.
 *
 * We create a function that wraps this so we can change what is returned by
 * localize in a node environment where locale may change. This is useful
 * for the web-email-server, where we cannot hard-refresh when changing locales.
 */
export const getIsoToCountryNames = (
  strictLocalize: StrictLocalizeFunction
) => {
  const ISO_TO_COUNTRY_NAME_UNSORTED = {
    AFG: strictLocalize({
      id: "QBh3su",
      defaultMessage: "Afghanistan",
      description: { text: "Country name" },
    }),
    ALA: strictLocalize({
      id: "vo6xQo",
      defaultMessage: "Åland Islands",
      description: { text: "Country name" },
    }),
    ALB: strictLocalize({
      id: "Gujw38",
      defaultMessage: "Albania",
      description: { text: "Country name" },
    }),
    DZA: strictLocalize({
      id: "2KJJ7X",
      defaultMessage: "Algeria",
      description: { text: "Country name" },
    }),
    ASM: strictLocalize({
      id: "RuWNAO",
      defaultMessage: "American Samoa",
      description: { text: "Country name" },
    }),
    AND: strictLocalize({
      id: "93njYI",
      defaultMessage: "Andorra",
      description: { text: "Country name" },
    }),
    AGO: strictLocalize({
      id: "vF3mkm",
      defaultMessage: "Angola",
      description: { text: "Country name" },
    }),
    AIA: strictLocalize({
      id: "/Xv8iQ",
      defaultMessage: "Anguilla",
      description: { text: "Country name" },
    }),
    ATA: strictLocalize({
      id: "v+YnqU",
      defaultMessage: "Antarctica",
      description: { text: "Country name" },
    }),
    ATG: strictLocalize({
      id: "NHAJW1",
      defaultMessage: "Antigua and Barbuda",
      description: { text: "Country name" },
    }),
    ARG: strictLocalize({
      id: "DHx4Fi",
      defaultMessage: "Argentina",
      description: { text: "Country name" },
    }),
    ARM: strictLocalize({
      id: "+ioUue",
      defaultMessage: "Armenia",
      description: { text: "Country name" },
    }),
    ABW: strictLocalize({
      id: "CWtTCF",
      defaultMessage: "Aruba",
      description: { text: "Country name" },
    }),
    AUS: strictLocalize({
      id: "wi3n+L",
      defaultMessage: "Australia",
      description: { text: "Country name" },
    }),
    AUT: strictLocalize({
      id: "n3QQnp",
      defaultMessage: "Austria",
      description: { text: "Country name" },
    }),
    AZE: strictLocalize({
      id: "I+AeNv",
      defaultMessage: "Azerbaijan",
      description: { text: "Country name" },
    }),
    BHS: strictLocalize({
      id: "p52X6h",
      defaultMessage: "Bahamas",
      description: { text: "Country name" },
    }),
    BHR: strictLocalize({
      id: "LA1YkY",
      defaultMessage: "Bahrain",
      description: { text: "Country name" },
    }),
    BGD: strictLocalize({
      id: "wGr0qF",
      defaultMessage: "Bangladesh",
      description: { text: "Country name" },
    }),
    BRB: strictLocalize({
      id: "nnXtJv",
      defaultMessage: "Barbados",
      description: { text: "Country name" },
    }),
    BLR: strictLocalize({
      id: "2kZkX/",
      defaultMessage: "Belarus",
      description: { text: "Country name" },
    }),
    BEL: strictLocalize({
      id: "SGul1d",
      defaultMessage: "Belgium",
      description: { text: "Country name" },
    }),
    BLZ: strictLocalize({
      id: "v/jwm9",
      defaultMessage: "Belize",
      description: { text: "Country name" },
    }),
    BEN: strictLocalize({
      id: "qIodyS",
      defaultMessage: "Benin",
      description: { text: "Country name" },
    }),
    BMU: strictLocalize({
      id: "pO16Lq",
      defaultMessage: "Bermuda",
      description: { text: "Country name" },
    }),
    BTN: strictLocalize({
      id: "UiiUyp",
      defaultMessage: "Bhutan",
      description: { text: "Country name" },
    }),
    BOL: strictLocalize({
      id: "0u8Xe7",
      defaultMessage: "Bolivia",
      description: { text: "Country name" },
    }),
    BES: strictLocalize({
      id: "2+a/1u",
      defaultMessage: "Bonaire, Saint Eustatius and Saba",
      description: { text: "Country name" },
    }),
    BIH: strictLocalize({
      id: "Lfkg6l",
      defaultMessage: "Bosnia and Herzegovina",
      description: { text: "Country name" },
    }),
    BWA: strictLocalize({
      id: "ufWv10",
      defaultMessage: "Botswana",
      description: { text: "Country name" },
    }),
    BVT: strictLocalize({
      id: "WgLEE7",
      defaultMessage: "Bouvet Island",
      description: { text: "Country name" },
    }),
    BRA: strictLocalize({
      id: "Na6Zk+",
      defaultMessage: "Brazil",
      description: { text: "Country name" },
    }),
    VGB: strictLocalize({
      id: "ozel3F",
      defaultMessage: "British Virgin Islands",
      description: { text: "Country name" },
    }),
    IOT: strictLocalize({
      id: "4zu/yh",
      defaultMessage: "British Indian Ocean Territory",
      description: { text: "Country name" },
    }),
    BRN: strictLocalize({
      id: "q0Yu0p",
      defaultMessage: "Brunei Darussalam",
      description: { text: "Country name" },
    }),
    BGR: strictLocalize({
      id: "dL0aUo",
      defaultMessage: "Bulgaria",
      description: { text: "Country name" },
    }),
    BFA: strictLocalize({
      id: "SC1iNB",
      defaultMessage: "Burkina Faso",
      description: { text: "Country name" },
    }),
    BDI: strictLocalize({
      id: "aqoo9+",
      defaultMessage: "Burundi",
      description: { text: "Country name" },
    }),
    KHM: strictLocalize({
      id: "UWqYoK",
      defaultMessage: "Cambodia",
      description: { text: "Country name" },
    }),
    CMR: strictLocalize({
      id: "y9+KMd",
      defaultMessage: "Cameroon",
      description: { text: "Country name" },
    }),
    CAN: strictLocalize({
      id: "Y43l6H",
      defaultMessage: "Canada",
      description: { text: "Country name" },
    }),
    CPV: strictLocalize({
      id: "3dwDVs",
      defaultMessage: "Cape Verde",
      description: { text: "Country name" },
    }),
    CYM: strictLocalize({
      id: "pUwxTl",
      defaultMessage: "Cayman Islands",
      description: { text: "Country name" },
    }),
    CAF: strictLocalize({
      id: "JuPwYl",
      defaultMessage: "Central African Republic",
      description: { text: "Country name" },
    }),
    TCD: strictLocalize({
      id: "vocmjG",
      defaultMessage: "Chad",
      description: { text: "Country name" },
    }),
    CHL: strictLocalize({
      id: "6YvCgQ",
      defaultMessage: "Chile",
      description: { text: "Country name" },
    }),
    CHN: strictLocalize({
      id: "Ghjs7e",
      defaultMessage: "China",
      description: { text: "Country name" },
    }),
    HKG: strictLocalize({
      id: "+1B2VQ",
      defaultMessage: "Hong Kong",
      description: { text: "Country name" },
    }),
    MAC: strictLocalize({
      id: "ZVbkZY",
      defaultMessage: "Macao",
      description: { text: "Country name" },
    }),
    CXR: strictLocalize({
      id: "1N7EQj",
      defaultMessage: "Christmas Island",
      description: { text: "Country name" },
    }),
    CCK: strictLocalize({
      id: "ooF4Qj",
      defaultMessage: "Cocos (Keeling) Islands",
      description: { text: "Country name" },
    }),
    COL: strictLocalize({
      id: "NvgMG6",
      defaultMessage: "Colombia",
      description: { text: "Country name" },
    }),
    COM: strictLocalize({
      id: "AurtzJ",
      defaultMessage: "Comoros",
      description: { text: "Country name" },
    }),
    COG: strictLocalize({
      id: "jqAucg",
      defaultMessage: "Republic of the Congo",
      description: { text: "Country name" },
    }),
    COD: strictLocalize({
      id: "VLuGdr",
      defaultMessage: "Democratic Republic of the Congo",
      description: { text: "Country name" },
    }),
    COK: strictLocalize({
      id: "icHAs2",
      defaultMessage: "Cook Islands",
      description: { text: "Country name" },
    }),
    CRI: strictLocalize({
      id: "Bg3Smm",
      defaultMessage: "Costa Rica",
      description: { text: "Country name" },
    }),
    CIV: strictLocalize({
      id: "tVN00+",
      defaultMessage: "Côte d’Ivoire",
      description: { text: "Country name" },
    }),
    HRV: strictLocalize({
      id: "sKVEhc",
      defaultMessage: "Croatia",
      description: { text: "Country name" },
    }),
    CUB: strictLocalize({
      id: "O4o1xo",
      defaultMessage: "Cuba",
      description: { text: "Country name" },
    }),
    CUW: strictLocalize({
      id: "56tgoN",
      defaultMessage: "Curaçao",
      description: { text: "Country name" },
    }),
    CYP: strictLocalize({
      id: "74C1kJ",
      defaultMessage: "Cyprus",
      description: { text: "Country name" },
    }),
    CZE: strictLocalize({
      id: "LlNh8P",
      defaultMessage: "Czechia",
      description: { text: "Country name" },
    }),
    DNK: strictLocalize({
      id: "wwP+59",
      defaultMessage: "Denmark",
      description: { text: "Country name" },
    }),
    DJI: strictLocalize({
      id: "CY07jY",
      defaultMessage: "Djibouti",
      description: { text: "Country name" },
    }),
    DMA: strictLocalize({
      id: "TwUQfn",
      defaultMessage: "Dominica",
      description: { text: "Country name" },
    }),
    DOM: strictLocalize({
      id: "R9KcDS",
      defaultMessage: "Dominican Republic",
      description: { text: "Country name" },
    }),
    ECU: strictLocalize({
      id: "wRGLHE",
      defaultMessage: "Ecuador",
      description: { text: "Country name" },
    }),
    EGY: strictLocalize({
      id: "8Km2x9",
      defaultMessage: "Egypt",
      description: { text: "Country name" },
    }),
    SLV: strictLocalize({
      id: "8VddLG",
      defaultMessage: "El Salvador",
      description: { text: "Country name" },
    }),
    GNQ: strictLocalize({
      id: "MrUUgI",
      defaultMessage: "Equatorial Guinea",
      description: { text: "Country name" },
    }),
    ERI: strictLocalize({
      id: "rZ4Na4",
      defaultMessage: "Eritrea",
      description: { text: "Country name" },
    }),
    EST: strictLocalize({
      id: "a+D4op",
      defaultMessage: "Estonia",
      description: { text: "Country name" },
    }),
    ETH: strictLocalize({
      id: "/w8ktd",
      defaultMessage: "Ethiopia",
      description: { text: "Country name" },
    }),
    FLK: strictLocalize({
      id: "KryyFA",
      defaultMessage: "Falkland Islands",
      description: { text: "Country name" },
    }),
    FRO: strictLocalize({
      id: "vYAqto",
      defaultMessage: "Faroe Islands",
      description: { text: "Country name" },
    }),
    FJI: strictLocalize({
      id: "5OViyD",
      defaultMessage: "Fiji",
      description: { text: "Country name" },
    }),
    FIN: strictLocalize({
      id: "krN2E7",
      defaultMessage: "Finland",
      description: { text: "Country name" },
    }),
    FRA: strictLocalize({
      id: "TMfPFS",
      defaultMessage: "France",
      description: { text: "Country name" },
    }),
    GUF: strictLocalize({
      id: "gtgEUW",
      defaultMessage: "French Guiana",
      description: { text: "Country name" },
    }),
    PYF: strictLocalize({
      id: "eY8VTj",
      defaultMessage: "French Polynesia",
      description: { text: "Country name" },
    }),
    ATF: strictLocalize({
      id: "bDccaJ",
      defaultMessage: "French Southern Territories",
      description: { text: "Country name" },
    }),
    GAB: strictLocalize({
      id: "r4x+33",
      defaultMessage: "Gabon",
      description: { text: "Country name" },
    }),
    GMB: strictLocalize({
      id: "s2Sy+v",
      defaultMessage: "Gambia",
      description: { text: "Country name" },
    }),
    GEO: strictLocalize({
      id: "WH7zS6",
      defaultMessage: "Georgia",
      description: { text: "Country name" },
    }),
    DEU: strictLocalize({
      id: "Jg9P3N",
      defaultMessage: "Germany",
      description: { text: "Country name" },
    }),
    GHA: strictLocalize({
      id: "sacbC3",
      defaultMessage: "Ghana",
      description: { text: "Country name" },
    }),
    GIB: strictLocalize({
      id: "M75o+d",
      defaultMessage: "Gibraltar",
      description: { text: "Country name" },
    }),
    GRC: strictLocalize({
      id: "dFXNeW",
      defaultMessage: "Greece",
      description: { text: "Country name" },
    }),
    GRL: strictLocalize({
      id: "wGMus1",
      defaultMessage: "Greenland",
      description: { text: "Country name" },
    }),
    GRD: strictLocalize({
      id: "hwFE5k",
      defaultMessage: "Grenada",
      description: { text: "Country name" },
    }),
    GLP: strictLocalize({
      id: "2hFu0m",
      defaultMessage: "Guadeloupe",
      description: { text: "Country name" },
    }),
    GUM: strictLocalize({
      id: "H548hZ",
      defaultMessage: "Guam",
      description: { text: "Country name" },
    }),
    GTM: strictLocalize({
      id: "FRrzA5",
      defaultMessage: "Guatemala",
      description: { text: "Country name" },
    }),
    GGY: strictLocalize({
      id: "nGDB07",
      defaultMessage: "Guernsey",
      description: { text: "Country name" },
    }),
    GIN: strictLocalize({
      id: "+mu5pQ",
      defaultMessage: "Guinea",
      description: { text: "Country name" },
    }),
    GNB: strictLocalize({
      id: "wZ3yaX",
      defaultMessage: "Guinea-Bissau",
      description: { text: "Country name" },
    }),
    GUY: strictLocalize({
      id: "GXyMEZ",
      defaultMessage: "Guyana",
      description: { text: "Country name" },
    }),
    HTI: strictLocalize({
      id: "LGCnXC",
      defaultMessage: "Haiti",
      description: { text: "Country name" },
    }),
    HMD: strictLocalize({
      id: "AMuxaH",
      defaultMessage: "Heard and Mcdonald Islands",
      description: { text: "Country name" },
    }),
    VAT: strictLocalize({
      id: "161T2t",
      defaultMessage: "Vatican City",
      description: { text: "Country name" },
    }),
    HND: strictLocalize({
      id: "OoooPP",
      defaultMessage: "Honduras",
      description: { text: "Country name" },
    }),
    HUN: strictLocalize({
      id: "vAhBtw",
      defaultMessage: "Hungary",
      description: { text: "Country name" },
    }),
    ISL: strictLocalize({
      id: "gsrojz",
      defaultMessage: "Iceland",
      description: { text: "Country name" },
    }),
    IND: strictLocalize({
      id: "OuqHJV",
      defaultMessage: "India",
      description: { text: "Country name" },
    }),
    IDN: strictLocalize({
      id: "mFgQr6",
      defaultMessage: "Indonesia",
      description: { text: "Country name" },
    }),
    IRN: strictLocalize({
      id: "j9iFWQ",
      defaultMessage: "Iran, Islamic Republic of",
      description: { text: "Country name" },
    }),
    IRQ: strictLocalize({
      id: "8NSGvE",
      defaultMessage: "Iraq",
      description: { text: "Country name" },
    }),
    IRL: strictLocalize({
      id: "KyFlH3",
      defaultMessage: "Ireland",
      description: { text: "Country name" },
    }),
    IMN: strictLocalize({
      id: "8RDJ7o",
      defaultMessage: "Isle of Man",
      description: { text: "Country name" },
    }),
    ISR: strictLocalize({
      id: "0oDO5k",
      defaultMessage: "Israel",
      description: { text: "Country name" },
    }),
    ITA: strictLocalize({
      id: "2cGE7a",
      defaultMessage: "Italy",
      description: { text: "Country name" },
    }),
    JAM: strictLocalize({
      id: "mnQosS",
      defaultMessage: "Jamaica",
      description: { text: "Country name" },
    }),
    JPN: strictLocalize({
      id: "4eo8lD",
      defaultMessage: "Japan",
      description: { text: "Country name" },
    }),
    JEY: strictLocalize({
      id: "7zLoLT",
      defaultMessage: "Jersey",
      description: { text: "Country name" },
    }),
    JOR: strictLocalize({
      id: "6G86jj",
      defaultMessage: "Jordan",
      description: { text: "Country name" },
    }),
    KAZ: strictLocalize({
      id: "jjsCdD",
      defaultMessage: "Kazakhstan",
      description: { text: "Country name" },
    }),
    KEN: strictLocalize({
      id: "YUI/xi",
      defaultMessage: "Kenya",
      description: { text: "Country name" },
    }),
    KIR: strictLocalize({
      id: "nBiHtL",
      defaultMessage: "Kiribati",
      description: { text: "Country name" },
    }),
    PRK: strictLocalize({
      id: "DpLPbT",
      defaultMessage: "North Korea",
      description: { text: "Country name" },
    }),
    KOR: strictLocalize({
      id: "L71Kgl",
      defaultMessage: "South Korea",
      description: { text: "Country name" },
    }),
    XKX: strictLocalize({
      id: "zZZv+D",
      defaultMessage: "Kosovo",
      description: { text: "Country name" },
    }),
    KWT: strictLocalize({
      id: "BwYt5E",
      defaultMessage: "Kuwait",
      description: { text: "Country name" },
    }),
    KGZ: strictLocalize({
      id: "S1SlKr",
      defaultMessage: "Kyrgyzstan",
      description: { text: "Country name" },
    }),
    LAO: strictLocalize({
      id: "170HHi",
      defaultMessage: "Laos",
      description: { text: "Country name" },
    }),
    LVA: strictLocalize({
      id: "TAHmAF",
      defaultMessage: "Latvia",
      description: { text: "Country name" },
    }),
    LBN: strictLocalize({
      id: "N5tzBg",
      defaultMessage: "Lebanon",
      description: { text: "Country name" },
    }),
    LSO: strictLocalize({
      id: "Be+iGc",
      defaultMessage: "Lesotho",
      description: { text: "Country name" },
    }),
    LBR: strictLocalize({
      id: "cJ1AN2",
      defaultMessage: "Liberia",
      description: { text: "Country name" },
    }),
    LBY: strictLocalize({
      id: "duL7f1",
      defaultMessage: "Libya",
      description: { text: "Country name" },
    }),
    LIE: strictLocalize({
      id: "EBiOjC",
      defaultMessage: "Liechtenstein",
      description: { text: "Country name" },
    }),
    LTU: strictLocalize({
      id: "RwSpZ1",
      defaultMessage: "Lithuania",
      description: { text: "Country name" },
    }),
    LUX: strictLocalize({
      id: "RoyikP",
      defaultMessage: "Luxembourg",
      description: { text: "Country name" },
    }),
    MKD: strictLocalize({
      id: "+Y+/GJ",
      defaultMessage: "Macedonia, Republic",
      description: { text: "Country name" },
    }),
    MDG: strictLocalize({
      id: "CqTXi8",
      defaultMessage: "Madagascar",
      description: { text: "Country name" },
    }),
    MWI: strictLocalize({
      id: "D/jODG",
      defaultMessage: "Malawi",
      description: { text: "Country name" },
    }),
    MYS: strictLocalize({
      id: "jR4Ryv",
      defaultMessage: "Malaysia",
      description: { text: "Country name" },
    }),
    MDV: strictLocalize({
      id: "t+lxSY",
      defaultMessage: "Maldives",
      description: { text: "Country name" },
    }),
    MLI: strictLocalize({
      id: "xRkAYP",
      defaultMessage: "Mali",
      description: { text: "Country name" },
    }),
    MLT: strictLocalize({
      id: "w+9rqk",
      defaultMessage: "Malta",
      description: { text: "Country name" },
    }),
    MHL: strictLocalize({
      id: "b5nDgZ",
      defaultMessage: "Marshall Islands",
      description: { text: "Country name" },
    }),
    MTQ: strictLocalize({
      id: "YNK13S",
      defaultMessage: "Martinique",
      description: { text: "Country name" },
    }),
    MRT: strictLocalize({
      id: "VhIhYa",
      defaultMessage: "Mauritania",
      description: { text: "Country name" },
    }),
    MUS: strictLocalize({
      id: "UFg4RN",
      defaultMessage: "Mauritius",
      description: { text: "Country name" },
    }),
    MYT: strictLocalize({
      id: "2w8doT",
      defaultMessage: "Mayotte",
      description: { text: "Country name" },
    }),
    MEX: strictLocalize({
      id: "x+z7J9",
      defaultMessage: "Mexico",
      description: { text: "Country name" },
    }),
    FSM: strictLocalize({
      id: "NDGZbf",
      defaultMessage: "Micronesia, Federated States",
      description: { text: "Country name" },
    }),
    MDA: strictLocalize({
      id: "aJcUCW",
      defaultMessage: "Moldova",
      description: { text: "Country name" },
    }),
    MCO: strictLocalize({
      id: "/BdZr9",
      defaultMessage: "Monaco",
      description: { text: "Country name" },
    }),
    MNG: strictLocalize({
      id: "vsuVAf",
      defaultMessage: "Mongolia",
      description: { text: "Country name" },
    }),
    MNE: strictLocalize({
      id: "0pMo08",
      defaultMessage: "Montenegro",
      description: { text: "Country name" },
    }),
    MSR: strictLocalize({
      id: "22YmQ7",
      defaultMessage: "Montserrat",
      description: { text: "Country name" },
    }),
    MAR: strictLocalize({
      id: "Ahm3S5",
      defaultMessage: "Morocco",
      description: { text: "Country name" },
    }),
    MOZ: strictLocalize({
      id: "iB8yfD",
      defaultMessage: "Mozambique",
      description: { text: "Country name" },
    }),
    MMR: strictLocalize({
      id: "6o298C",
      defaultMessage: "Myanmar",
      description: { text: "Country name" },
    }),
    NAM: strictLocalize({
      id: "m9VosY",
      defaultMessage: "Namibia",
      description: { text: "Country name" },
    }),
    NRU: strictLocalize({
      id: "FGevaQ",
      defaultMessage: "Nauru",
      description: { text: "Country name" },
    }),
    NPL: strictLocalize({
      id: "kX6yvj",
      defaultMessage: "Nepal",
      description: { text: "Country name" },
    }),
    NLD: strictLocalize({
      id: "HlpkCb",
      defaultMessage: "Netherlands",
      description: { text: "Country name" },
    }),
    ANT: strictLocalize({
      id: "PtSY6j",
      defaultMessage: "Netherlands Antilles",
      description: { text: "Country name" },
    }),
    NCL: strictLocalize({
      id: "TPATKd",
      defaultMessage: "New Caledonia",
      description: { text: "Country name" },
    }),
    NZL: strictLocalize({
      id: "xUHU/X",
      defaultMessage: "New Zealand",
      description: { text: "Country name" },
    }),
    NIC: strictLocalize({
      id: "at1lyZ",
      defaultMessage: "Nicaragua",
      description: { text: "Country name" },
    }),
    NER: strictLocalize({
      id: "6z1tLM",
      defaultMessage: "Niger",
      description: { text: "Country name" },
    }),
    NGA: strictLocalize({
      id: "baaOlU",
      defaultMessage: "Nigeria",
      description: { text: "Country name" },
    }),
    NIU: strictLocalize({
      id: "D0dDRz",
      defaultMessage: "Niue",
      description: { text: "Country name" },
    }),
    NFK: strictLocalize({
      id: "gGE+Mn",
      defaultMessage: "Norfolk Island",
      description: { text: "Country name" },
    }),
    MNP: strictLocalize({
      id: "Ywp73c",
      defaultMessage: "Northern Mariana Islands",
      description: { text: "Country name" },
    }),
    NOR: strictLocalize({
      id: "M3MKYO",
      defaultMessage: "Norway",
      description: { text: "Country name" },
    }),
    OMN: strictLocalize({
      id: "SKo0T4",
      defaultMessage: "Oman",
      description: { text: "Country name" },
    }),
    PAK: strictLocalize({
      id: "wQwZnm",
      defaultMessage: "Pakistan",
      description: { text: "Country name" },
    }),
    PLW: strictLocalize({
      id: "CUFYgB",
      defaultMessage: "Palau",
      description: { text: "Country name" },
    }),
    PSE: strictLocalize({
      id: "zitCvU",
      defaultMessage: "Palestine",
      description: { text: "Country name" },
    }),
    PAN: strictLocalize({
      id: "gK11Ha",
      defaultMessage: "Panama",
      description: { text: "Country name" },
    }),
    PNG: strictLocalize({
      id: "YVDmvP",
      defaultMessage: "Papua New Guinea",
      description: { text: "Country name" },
    }),
    PRY: strictLocalize({
      id: "tbvLcT",
      defaultMessage: "Paraguay",
      description: { text: "Country name" },
    }),
    PER: strictLocalize({
      id: "IMBhgU",
      defaultMessage: "Peru",
      description: { text: "Country name" },
    }),
    PHL: strictLocalize({
      id: "cPusxn",
      defaultMessage: "Philippines",
      description: { text: "Country name" },
    }),
    PCN: strictLocalize({
      id: "xx6tRk",
      defaultMessage: "Pitcairn",
      description: { text: "Country name" },
    }),
    POL: strictLocalize({
      id: "m+SGaR",
      defaultMessage: "Poland",
      description: { text: "Country name" },
    }),
    PRT: strictLocalize({
      id: "HEtxVV",
      defaultMessage: "Portugal",
      description: { text: "Country name" },
    }),
    PRI: strictLocalize({
      id: "lb/Sp4",
      defaultMessage: "Puerto Rico",
      description: { text: "Country name" },
    }),
    QAT: strictLocalize({
      id: "foR1pI",
      defaultMessage: "Qatar",
      description: { text: "Country name" },
    }),
    REU: strictLocalize({
      id: "OFvw8Y",
      defaultMessage: "Réunion",
      description: { text: "Country name" },
    }),
    ROU: strictLocalize({
      id: "txpaaH",
      defaultMessage: "Romania",
      description: { text: "Country name" },
    }),
    RUS: strictLocalize({
      id: "Jx+c2V",
      defaultMessage: "Russia",
      description: { text: "Country name" },
    }),
    RWA: strictLocalize({
      id: "M5Kh/B",
      defaultMessage: "Rwanda",
      description: { text: "Country name" },
    }),
    BLM: strictLocalize({
      id: "D8ZPWe",
      defaultMessage: "Saint-Barthélemy",
      description: { text: "Country name" },
    }),
    SHN: strictLocalize({
      id: "nacNQW",
      defaultMessage: "Saint Helena",
      description: { text: "Country name" },
    }),
    KNA: strictLocalize({
      id: "Lr71H9",
      defaultMessage: "Saint Kitts and Nevis",
      description: { text: "Country name" },
    }),
    LCA: strictLocalize({
      id: "kaHMwy",
      defaultMessage: "Saint Lucia",
      description: { text: "Country name" },
    }),
    MAF: strictLocalize({
      id: "HpePox",
      defaultMessage: "Saint-Martin",
      description: { text: "Country name" },
    }),
    SPM: strictLocalize({
      id: "htE+3A",
      defaultMessage: "Saint Pierre and Miquelon",
      description: { text: "Country name" },
    }),
    VCT: strictLocalize({
      id: "IieMUv",
      defaultMessage: "Saint Vincent and Grenadines",
      description: { text: "Country name" },
    }),
    WSM: strictLocalize({
      id: "PqB5H7",
      defaultMessage: "Samoa",
      description: { text: "Country name" },
    }),
    SMR: strictLocalize({
      id: "sX5Nh2",
      defaultMessage: "San Marino",
      description: { text: "Country name" },
    }),
    STP: strictLocalize({
      id: "8ZnG/t",
      defaultMessage: "Sao Tome and Principe",
      description: { text: "Country name" },
    }),
    SAU: strictLocalize({
      id: "MLFihM",
      defaultMessage: "Saudi Arabia",
      description: { text: "Country name" },
    }),
    SEN: strictLocalize({
      id: "p37RgC",
      defaultMessage: "Senegal",
      description: { text: "Country name" },
    }),
    SRB: strictLocalize({
      id: "hg7dgx",
      defaultMessage: "Serbia",
      description: { text: "Country name" },
    }),
    SYC: strictLocalize({
      id: "IsFZWA",
      defaultMessage: "Seychelles",
      description: { text: "Country name" },
    }),
    SLE: strictLocalize({
      id: "HJ6rPH",
      defaultMessage: "Sierra Leone",
      description: { text: "Country name" },
    }),
    SGP: strictLocalize({
      id: "cBofoc",
      defaultMessage: "Singapore",
      description: { text: "Country name" },
    }),
    SXM: strictLocalize({
      id: "H600gx",
      defaultMessage: "Sint Maarten",
      description: { text: "Country name" },
    }),
    SVK: strictLocalize({
      id: "QO0zUl",
      defaultMessage: "Slovakia",
      description: { text: "Country name" },
    }),
    SVN: strictLocalize({
      id: "Eb7TBP",
      defaultMessage: "Slovenia",
      description: { text: "Country name" },
    }),
    SLB: strictLocalize({
      id: "bc2C7j",
      defaultMessage: "Solomon Islands",
      description: { text: "Country name" },
    }),
    SOM: strictLocalize({
      id: "AJjHB9",
      defaultMessage: "Somalia",
      description: { text: "Country name" },
    }),
    ZAF: strictLocalize({
      id: "F4JAu9",
      defaultMessage: "South Africa",
      description: { text: "Country name" },
    }),
    SGS: strictLocalize({
      id: "sK4vNT",
      defaultMessage: "South Georgia and the South Sandwich Islands",
      description: { text: "Country name" },
    }),
    SSD: strictLocalize({
      id: "YcgnTs",
      defaultMessage: "South Sudan",
      description: { text: "Country name" },
    }),
    ESP: strictLocalize({
      id: "73A2s8",
      defaultMessage: "Spain",
      description: { text: "Country name" },
    }),
    LKA: strictLocalize({
      id: "Ug8mB3",
      defaultMessage: "Sri Lanka",
      description: { text: "Country name" },
    }),
    SDN: strictLocalize({
      id: "eaJbCT",
      defaultMessage: "Sudan",
      description: { text: "Country name" },
    }),
    SUR: strictLocalize({
      id: "xNQroC",
      defaultMessage: "Suriname",
      description: { text: "Country name" },
    }),
    SJM: strictLocalize({
      id: "rIFue7",
      defaultMessage: "Svalbard and Jan Mayen Islands",
      description: { text: "Country name" },
    }),
    SWZ: strictLocalize({
      id: "qMaAmt",
      defaultMessage: "Eswatini",
      description: { text: "Country name" },
    }),
    SWE: strictLocalize({
      id: "/iWlKM",
      defaultMessage: "Sweden",
      description: { text: "Country name" },
    }),
    CHE: strictLocalize({
      id: "IhJIY6",
      defaultMessage: "Switzerland",
      description: { text: "Country name" },
    }),
    SYR: strictLocalize({
      id: "v2mAHZ",
      defaultMessage: "Syria",
      description: { text: "Country name" },
    }),
    TWN: strictLocalize({
      id: "UcF9Qa",
      defaultMessage: "Taiwan",
      description: { text: "Country name" },
    }),
    TJK: strictLocalize({
      id: "Hr++1T",
      defaultMessage: "Tajikistan",
      description: { text: "Country name" },
    }),
    TZA: strictLocalize({
      id: "t0rZxq",
      defaultMessage: "Tanzania, United Republic of",
      description: { text: "Country name" },
    }),
    THA: strictLocalize({
      id: "T76v70",
      defaultMessage: "Thailand",
      description: { text: "Country name" },
    }),
    TLS: strictLocalize({
      id: "4jPy/Z",
      defaultMessage: "Timor-Leste",
      description: { text: "Country name" },
    }),
    TGO: strictLocalize({
      id: "mf8QCj",
      defaultMessage: "Togo",
      description: { text: "Country name" },
    }),
    TKL: strictLocalize({
      id: "mNpbEF",
      defaultMessage: "Tokelau",
      description: { text: "Country name" },
    }),
    TON: strictLocalize({
      id: "q7uDLw",
      defaultMessage: "Tonga",
      description: { text: "Country name" },
    }),
    TTO: strictLocalize({
      id: "j8Q6pf",
      defaultMessage: "Trinidad and Tobago",
      description: { text: "Country name" },
    }),
    TUN: strictLocalize({
      id: "OzhbZp",
      defaultMessage: "Tunisia",
      description: { text: "Country name" },
    }),
    TUR: strictLocalize({
      id: "R/DrWa",
      defaultMessage: "Turkey",
      description: { text: "Country name" },
    }),
    TKM: strictLocalize({
      id: "n8EqN4",
      defaultMessage: "Turkmenistan",
      description: { text: "Country name" },
    }),
    TCA: strictLocalize({
      id: "/VMImV",
      defaultMessage: "Turks and Caicos Islands",
      description: { text: "Country name" },
    }),
    TUV: strictLocalize({
      id: "k+hXba",
      defaultMessage: "Tuvalu",
      description: { text: "Country name" },
    }),
    UGA: strictLocalize({
      id: "lX0oob",
      defaultMessage: "Uganda",
      description: { text: "Country name" },
    }),
    UKR: strictLocalize({
      id: "LbrKZt",
      defaultMessage: "Ukraine",
      description: { text: "Country name" },
    }),
    ARE: strictLocalize({
      id: "JJ+wjh",
      defaultMessage: "United Arab Emirates",
      description: { text: "Country name" },
    }),
    GBR: strictLocalize({
      id: "uERgVc",
      defaultMessage: "United Kingdom",
      description: { text: "Country name" },
    }),
    USA: strictLocalize({
      id: "zwvKKX",
      defaultMessage: "United States of America",
      description: { text: "Country name" },
    }),
    UMI: strictLocalize({
      id: "rzVr7y",
      defaultMessage: "US Minor Outlying Islands",
      description: { text: "Country name" },
    }),
    URY: strictLocalize({
      id: "sJmhho",
      defaultMessage: "Uruguay",
      description: { text: "Country name" },
    }),
    UZB: strictLocalize({
      id: "ZMi/t6",
      defaultMessage: "Uzbekistan",
      description: { text: "Country name" },
    }),
    VUT: strictLocalize({
      id: "ZBYSI6",
      defaultMessage: "Vanuatu",
      description: { text: "Country name" },
    }),
    VEN: strictLocalize({
      id: "cskFJu",
      defaultMessage: "Venezuela",
      description: { text: "Country name" },
    }),
    VNM: strictLocalize({
      id: "hVDqQx",
      defaultMessage: "Vietnam",
      description: { text: "Country name" },
    }),
    VIR: strictLocalize({
      id: "m9Z1z1",
      defaultMessage: "Virgin Islands",
      description: { text: "Country name" },
    }),
    WLF: strictLocalize({
      id: "UyXh3u",
      defaultMessage: "Wallis and Futuna Islands",
      description: { text: "Country name" },
    }),
    ESH: strictLocalize({
      id: "PKA+Nm",
      defaultMessage: "Western Sahara",
      description: { text: "Country name" },
    }),
    YEM: strictLocalize({
      id: "a5cHe8",
      defaultMessage: "Yemen",
      description: { text: "Country name" },
    }),
    ZMB: strictLocalize({
      id: "xlbusQ",
      defaultMessage: "Zambia",
      description: { text: "Country name" },
    }),
    ZWE: strictLocalize({
      id: "QR3g6K",
      defaultMessage: "Zimbabwe",
      description: { text: "Country name" },
    }),
  };
  const ISO_TO_COUNTRY_NAME = Object.fromEntries(
    Object.entries(ISO_TO_COUNTRY_NAME_UNSORTED).sort(([, a], [, b]) =>
      a.localeCompare(b, LocalizationStore.get().locale)
    )
  ) as typeof ISO_TO_COUNTRY_NAME_UNSORTED;

  return omit(ISO_TO_COUNTRY_NAME, ["CUB", "PRK", "SYR", "IRN"]);
};

export const ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS = (
  strictLocalize: StrictLocalizeFunction
) => getIsoToCountryNames(strictLocalize);
